import './style.modules.scss'

import {
  BrandIcon,
  BrandIconId,
  Link,
  LinkSize,
  LinkVariant,
  MaterialIcon,
  MaterialIconSize
} from '@ampcore/adel-react'
import { AmpcoreMaterialIconId } from '@ampcore/ampcom-contracts'
import { UserResponse } from '@ampcore/client-side-services'
import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { makeUrlRelative } from '../../lib/CfgApi/makeUrlRelative'
import { AdvisorData } from '../../types/AdvisorData.type'
import { MenuItem } from '../../types/MenuItem.type'
import { NavAnalyticsClickHandler } from '../../types/NavAnalytics.type'
import { UserStatus } from '../../types/UserStatus.enum'
import { ConditionalLink, ConditionalLinkProps } from '../Link/ConditionalLink'
import { MenuButton } from '../MenuButton/MenuButton'
import { MostVisitedLinks } from '../MostVisitedLinks/MostVisitedLinks'
import { SearchBox } from '../SearchBox/SearchBox'
import { SearchPanelProps } from '../SearchPanel/SearchPanel'
import { AppMenuMobileMenuItem } from './AppMenuMobileMenuItem'

export type AppMenuMobileProps = {
  userStatus?: UserStatus
  className?: string
  isOpen: boolean
  searchPanel?: SearchPanelProps
  menuItems?: MenuItem[]
  mobileBottomMenuItems: MenuItem[]
  mobileNavSectionRef: React.RefObject<HTMLDivElement>
  advisorData?: AdvisorData | null
  userData?: UserResponse | null
  testId?: string
  onMenuClick?: NavAnalyticsClickHandler
  onClose?: () => void
  logoHref?: string
  links?: Omit<ConditionalLinkProps, 'userStatus'>[]
  /** When false, renders inline instead of portaling to document.body. Useful inside shadow DOM. Defaults to true. */
  usePortal?: boolean
}

export const AppMenuMobile = (props: AppMenuMobileProps) => {
  const {
    userStatus,
    className,
    isOpen,
    searchPanel,
    menuItems,
    mobileBottomMenuItems,
    mobileNavSectionRef,
    advisorData,
    userData,
    testId,
    onMenuClick,
    onClose,
    logoHref,
    links
  } = props
  const usePortal = props.usePortal !== false
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [navigationTree, setNavigationTree] = useState<number[]>([])
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (isOpen && closeButtonRef.current) {
      // Use setTimeout to ensure focus is set after useFocusTrap's initial container focus
      setTimeout(() => {
        closeButtonRef.current?.focus()
      }, 1)
    }
  }, [isOpen])
  function getMenuItems(
    arr: MenuItem[],
    indexes: number[]
  ): MenuItem[] | undefined {
    let value = arr
    for (const index of indexes) {
      if (value[index].menuItems === undefined) {
        return undefined
      }
      value = value[index].menuItems!
    }
    return value
  }

  const handleSearchClick = () => {
    setIsSearchOpen(prev => !prev)
  }

  const handleBackClick = () => {
    setIsSearchOpen(false)
    setNavigationTree([])
  }

  const updateNavigationTree = (
    event: React.KeyboardEvent<HTMLAnchorElement> | React.MouseEvent,
    newIndexes: number[]
  ) => {
    setNavigationTree(previousIndexes => {
      const isSameIndex =
        JSON.stringify(previousIndexes) === JSON.stringify(newIndexes)

      // Collapse tier 2 menu on close
      if (newIndexes.length === 2 && isSameIndex) {
        event.preventDefault()
        return newIndexes.slice(0, -1)
      }

      const subMenuItems = getMenuItems(menuItems!, newIndexes)

      // Return previous indexes if there are no sub menu options
      if (subMenuItems?.length === 0) {
        return previousIndexes
      }

      // Expand tier 1 menu if it has children and is not open
      if (previousIndexes.length === 0 && subMenuItems) {
        event.preventDefault()
        return newIndexes
      }

      // Disable link if not tier 2 and has children
      if (
        !isSameIndex &&
        newIndexes.length !== 1 &&
        subMenuItems?.length !== 0
      ) {
        event.preventDefault()
      }

      return newIndexes
    })
  }

  const handleMenuKeyDown = (
    event: React.KeyboardEvent<HTMLAnchorElement>,
    indexes: number[]
  ) => {
    if (event.key === 'Enter') {
      updateNavigationTree(event, indexes)
    }
  }

  useEffect(() => {
    if (isSearchOpen) {
      // Query within the component's root to support both document and shadow DOM
      const root = mobileNavSectionRef.current?.getRootNode() as
        | Document
        | ShadowRoot
        | undefined
      const searchInput = (root || document).querySelector('input[name="sp_q"]')
      if (searchInput instanceof HTMLInputElement) {
        searchInput.focus()
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsSearchOpen(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isSearchOpen, mobileNavSectionRef])

  const handleMenuClick = (event: React.MouseEvent, indexes: number[]) => {
    updateNavigationTree(event, indexes)
  }

  if (!isOpen) {
    return null
  }

  const content = (
    <div
      className={`AppMenuMobile u-block u-flexAlignSelfStretch u-bgColorLight u-borderTop u-borderColorNeutral ${isSearchOpen ? 'is-searchOpen' : ''} ${className}`}
      ref={mobileNavSectionRef}
      aria-label="Mobile navigation menu"
      tabIndex={-1}
    >
      <div className="AppMenuMobile-header u-flex u-flexAlignItemsCenter u-flexJustifyBetween u-paddingVert8 u-paddingHoriz20 u-bgColorWhite">
        {onClose && (
          <MenuButton
            open={true}
            onClick={onClose}
            ref={closeButtonRef}
            testId={testId ? `${testId}-close-button` : undefined}
          />
        )}
        {logoHref && (
          <a href={logoHref} aria-label="Ameriprise Financial Home">
            <BrandIcon
              icon={BrandIconId.AMPF_LOGO}
              style={{
                width: '125px',
                height: '44px',
                fill: '#003f6b'
              }}
              spriteUrl={''}
              ariaLabel="Ameriprise Financial"
              testId={testId ? `${testId}-menu-logo` : undefined}
            />
          </a>
        )}
        <div className="AppMenuMobile-headerLinks u-flex u-flexAlignItemsCenter">
          {links &&
            userStatus &&
            links.map((link, index) => (
              <ConditionalLink
                key={link.url}
                {...link}
                userStatus={userStatus}
                href={makeUrlRelative(link.url)}
                analyticsName={link.analyticsName}
                size={LinkSize.SMALL}
                testId={testId ? `${testId}-link-${index}` : undefined}
                onClick={onMenuClick}
              />
            ))}
          {!links && userStatus !== UserStatus.CLIENT_ACTIVE && (
            <Link
              name="Log In"
              url={makeUrlRelative('https://www.ameriprise.com/client-login')}
              analyticsName="Log In"
              size={LinkSize.SMALL}
              variant={LinkVariant.SECONDARY}
              testId={testId ? `${testId}-log-in` : undefined}
              onClick={onMenuClick}
            />
          )}
          {!links &&
            userStatus === UserStatus.CLIENT_ACTIVE &&
            userData?.data?.firstName &&
            !userData?.data?.isOrgClient && (
              <Link
                name={`Hi, ${userData.data.firstName}`}
                url={makeUrlRelative(
                  'https://www.ameriprise.com/secure/profile'
                )}
                analyticsName="Secure Profile"
                size={LinkSize.SMALL}
                testId={testId ? `${testId}-secure-profile` : undefined}
              />
            )}
        </div>
      </div>
      <ul className="AppMenuMobile-tier1 AppMenuMobile-menuItems List List--selectable u-borderBottom0 u-margin0 u-bgColorWhite">
        {(navigationTree[0] !== undefined || isSearchOpen) && (
          <li>
            <button
              className="Link AppMenuMobile-backButton u-linkClean u-flex u-sizeFull u-paddingVert16 u-paddingLeft48 u-paddingRight20 u-bgColorWhite u-borderVert"
              onClick={handleBackClick}
              data-testid={testId ? `${testId}-back-button` : null}
            >
              <span className="u-marginLeft4 u-textSizeMsrm1">Back</span>
            </button>
          </li>
        )}
        {!isSearchOpen && menuItems && (
          <>
            {menuItems.map(
              (tier1Item, tier1Index) =>
                (navigationTree[0] === undefined ||
                  navigationTree[0] === tier1Index) && (
                  <AppMenuMobileMenuItem
                    key={tier1Item.name}
                    tier1Item={tier1Item}
                    tier1Index={tier1Index}
                    navigationTree={navigationTree}
                    handleMenuClick={handleMenuClick}
                    handleMenuKeyDown={handleMenuKeyDown}
                    onMenuClick={onMenuClick}
                  />
                )
            )}
          </>
        )}
      </ul>
      {searchPanel && isSearchOpen && (
        <div className="AppMenuMobile-searchPanel u-sizeConstrained">
          {searchPanel.searchBox && (
            <div className="u-paddingTop48">
              <SearchBox {...searchPanel.searchBox} />
            </div>
          )}
          {searchPanel.mostVisitedLinks && (
            <div className="u-paddingTop64">
              <MostVisitedLinks {...searchPanel.mostVisitedLinks} />
            </div>
          )}
        </div>
      )}
      {!isSearchOpen && (
        <div className="u-sizeConstrained">
          <ul className="List List--selectable u-borderBottom0 u-margin0 u-sm-paddingVert16 u-sm-paddingHoriz16 u-md-paddingVert24 u-md-paddingHoriz16 u-textCenter">
            {searchPanel?.searchBox && (
              <li className="List-item">
                <button
                  style={{ width: '100%', cursor: 'pointer' }}
                  className="u-block u-textCenter u-textSizeMsrm1 u-textColorActionBlue"
                  onClick={handleSearchClick}
                >
                  <MaterialIcon
                    className="u-marginRight8"
                    style={{ paddingTop: '3px' }}
                    icon={AmpcoreMaterialIconId.SEARCH}
                    size={MaterialIconSize.SIZE_16}
                    ariaLabel="search"
                  />
                  Search
                </button>
              </li>
            )}
            {mobileBottomMenuItems?.map((navItem, index) => (
              <li key={index} className="List-item u-flexJustifyCenter ">
                <Link
                  {...navItem}
                  url={makeUrlRelative(navItem.url)}
                  className="u-block u-textCenter u-sm-textSizeMsrm1 u-md-textSizeMsrm1"
                  onClick={onMenuClick}
                />
              </li>
            ))}
          </ul>

          <div className="u-marginLeft20 u-marginRight20">
            {userStatus === UserStatus.PROSPECT && advisorData?.url && (
              <>
                <hr className="Rule u-lg-hidden u-marginBottom32" />
                <p className="u-marginBottom32 u-textCenter u-sm-textSizeMsrm1">
                  Take the first step to a confident financial future. Contact{' '}
                  <b>{advisorData?.name}</b>
                </p>
                <Link
                  name="Request Consultation"
                  url={advisorData?.url}
                  variant={LinkVariant.PRIMARY}
                  className="u-sizeFull"
                  testId={testId ? `${testId}-request-consultation` : undefined}
                  onClick={onMenuClick}
                />
              </>
            )}
            {userStatus === UserStatus.CLIENT_ACTIVE && (
              <Link
                name="Return to My Accounts"
                url={makeUrlRelative(
                  'https://www.ameriprise.com/secure/overview'
                )}
                analyticsName="Return to My Accounts"
                variant={LinkVariant.SECONDARY}
                className="Button--block u-flexJustifyCenter u-marginTop48"
                testId={testId ? `${testId}-return-accounts` : undefined}
                onClick={onMenuClick}
              />
            )}
            {userStatus === UserStatus.PROSPECT && !advisorData?.url && (
              <Link
                name="Find an Advisor"
                url="https://www.ameripriseadvisors.com"
                analyticsName="Find an Advisor"
                variant={LinkVariant.PRIMARY}
                className="Button--block u-flexJustifyCenter u-marginTop48"
                testId={testId ? `${testId}-find-advisor` : undefined}
                onClick={onMenuClick}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )

  return usePortal ? createPortal(content, document.body) : content
}
